import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { useAuth } from "../auth/AuthProvider";
import { setSharedCompany } from "../auth/teamCompany";

const API_URL = process.env.REACT_APP_API_URL;

export const CompanyContext = createContext(null);

export const CompanyProvider = ({ children }) => {
  const auth = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(
    () => localStorage.getItem("activeCompanyId") || ""
  );
  const [isDemo, setIsDemo] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  // The backend refused this Olene account outright (403 that isn't the
  // subscription gate). PayClear lets any Olene account in today, so this is
  // defensive; App shows the "no access to PayClear" screen when it is set.
  const [accessDenied, setAccessDenied] = useState(false);

  // Separate companies-only refetch for after mutations (add/edit/delete company)
  const refetch = useCallback(async () => {
    if (!auth.user?.access_token) return;
    try {
      const res = await fetch(`${API_URL}/companies`, {
        headers: { Authorization: `Bearer ${auth.user.access_token}` },
      });
      if (res.ok) setCompanies(await res.json());
    } catch {}
  }, [auth.user?.access_token]);

  // Company details can change outside PayClear (account.olenesoft.com, other Olene apps; see
  // backend/olene_events.py), so refetch when the user comes back to the tab, at most every 30s.
  useEffect(() => {
    let last = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== "visible" || Date.now() - last < 30000) return;
      last = Date.now();
      refetch();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refetch]);

  // If auth finishes loading and the user is not logged in, stop the spinner immediately
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      setIsInitializing(false);
    }
  }, [auth.isLoading, auth.isAuthenticated]);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.access_token) return;

    const token = auth.user.access_token;

    const init = async () => {
      try {
        const res = await fetch(`${API_URL}/app/init`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const { subscription, is_demo, companies: companiesData } = await res.json();
          setSubscriptionData(subscription);
          setIsDemo(is_demo);
          setCompanies(companiesData);
        } else if (res.status === 403) {
          const body = await res.json().catch(() => null);
          const detail = body?.detail;
          const code = typeof detail === "object" ? detail?.error : null;
          if (code !== "subscription_required") setAccessDenied(true);
        }
      } catch {}
      setIsInitializing(false);
    };

    init();
  }, [auth.isAuthenticated, auth.user?.access_token, refetch]);

  const activeCompany =
    companies.find((c) => c.company_id === activeCompanyId) ||
    companies.find((c) => c.is_default) ||
    companies[0] ||
    null;

  // A company someone else shared (backend/acting.py): requests go as its owner, and the owner's
  // subscription is the one that counts. Set during render, not in an effect, so that pages'
  // own effects (which run first) already send it.
  const shared = activeCompany?.shared || null;
  setSharedCompany(shared ? activeCompany.company_id : null);
  // "owner" for the person's own companies; "admin" or "use" for shared ones.
  const accessLevel = shared ? shared.level : "owner";
  const effectiveSubscription = shared
    ? { subscriptionStatus: shared.subscribed ? "active" : "none", billingPlan: "", subscriptionEnd: "N/A",
        cancelAtPeriodEnd: false, is_admin: false, shared: true }
    : subscriptionData;

  const switchCompany = (companyId) => {
    setActiveCompanyId(companyId);
    localStorage.setItem("activeCompanyId", companyId);
  };

  // Staff support (pages/SupportStart.js): End in the banner, or the session ended elsewhere (the API
  // answers 403 support_ended). Back to the staff member's own company either way.
  const leaveSupport = useCallback(async (sessionId) => {
    if (sessionId && auth.user?.access_token) {
      await fetch(`${API_URL}/support/${encodeURIComponent(sessionId)}/end`, {
        method: "POST", headers: { Authorization: `Bearer ${auth.user.access_token}` },
      }).catch(() => {});
    }
    const own = companies.find((c) => !c.shared && c.is_default) || companies.find((c) => !c.shared);
    setActiveCompanyId(own ? own.company_id : "");
    localStorage.setItem("activeCompanyId", own ? own.company_id : "");
    refetch();
  }, [auth.user?.access_token, companies, refetch]);

  useEffect(() => {
    const ended = () => { if (activeCompany?.shared?.support) leaveSupport(null); };
    window.addEventListener("payclear:support-ended", ended);
    return () => window.removeEventListener("payclear:support-ended", ended);
  }, [activeCompany, leaveSupport]);

  const userGroups = (() => {
    try {
      const payload = auth.user?.access_token?.split(".")[1];
      return JSON.parse(atob(payload))["cognito:groups"] || [];
    } catch { return []; }
  })();

  return (
    <CompanyContext.Provider value={{
      companies, activeCompany, switchCompany, refetch, accessLevel, leaveSupport,
      isDemo: shared ? !shared.subscribed : isDemo, isInitializing,
      subscriptionData: effectiveSubscription, userGroups, accessDenied,
    }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used inside CompanyProvider");
  return ctx;
};
